import { useState } from 'react';
import { ApprovalInfo, MoldOrder, User } from '../../types/mold';
import UserProfileImage from '../KanbanBoard/UserProfileImage';
import NextAssigneeModal from './NextAssigneeModal';
import { useAuth } from '../../hooks/useAuth';
import { User as UserIcon } from 'lucide-react';
import ViewQuestionnaireButton from '../shared/ViewQuestionnaireButton';
import ApprovalListBox from './ApprovalListBox';
import MoldOrderStatus from '../shared/MoldOrderStatus';

interface NextAssigneeInfoProps {
  assignee: User | null | undefined;
  onChangeAssignee: (assignee: User) => void;
  assigneeList: Array<User>;
  isCurrentAssignee: boolean;
  approvalInfo?: ApprovalInfo;
  children?: React.ReactNode;
  moldOrder: MoldOrder;
}

export const NextAssigneeInfo: React.FC<NextAssigneeInfoProps> = ({
  assigneeList,
  assignee: defaultAssignee,
  onChangeAssignee,
  isCurrentAssignee,
  approvalInfo,
  children,
  moldOrder,
}) => {
  const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
  const [assignee, setAssignee] = useState<User>(defaultAssignee ?? assigneeList[0]);
  const { user } = useAuth();

  const handleAssigneeChange = (newAssigneeId: string) => {
    const newAssignee = assigneeList?.find((user) => user.id.toString() === newAssigneeId);
    if (newAssignee) {
      setAssignee(newAssignee);
      onChangeAssignee(newAssignee)
    }
  };

  // Helper function to check if current user is an approver for current slot
  const isCurrentUserApprover = () => {
    if (!approvalInfo || !user) return false;

    const currentSlot = approvalInfo.approverSlots.find(slot => slot.id === approvalInfo.assignedSlotId);
    if (!currentSlot || currentSlot.status !== 'pending') return false;

    // Check if user is assigned directly
    if (currentSlot.approverType === 'user') {
      return currentSlot.assignedUser?.id === user.user_id;
    }

    // Check if user has the required role
    if (currentSlot.approverType === 'role') {
      return user.role === currentSlot.assignedRole?.role;
    }

    return false;
  };

  // Helper function to determine if we should hide action buttons
  const shouldHideActionButtons = () => {
    // If approval is required and pending, hide action buttons for current assignee
    if (approvalInfo?.approvalRequired && approvalInfo.approvalStatus === 'pending') {
      return isCurrentAssignee && !isCurrentUserApprover();
    }
    return false;
  };

  // Helper function to get current assignee name
  const getCurrentAssigneeName = () => {
    return assignee?.fullname || assignee?.username || 'ไม่ระบุ';
  };

  // Helper function to render compact approval workflow
  const CompactApprovalWorkflow = () => {
    if (!approvalInfo?.approvalRequired || !approvalInfo.approverSlots.length) {
      return null;
    }

    return (
      <div className="p-5 border-2 border-slate-200 rounded-xl">
        {isCurrentAssignee && <MoldOrderStatusInformation />}
        {renderCurrentAssigneeInfo()}
        {/* Approval Process Section */}
        <ApprovalListBox
          user={user}
          approvalInfo={approvalInfo} />

        <div className="mt-4" data-cy="action-buttons">
          {shouldHideActionButtons() ? <ViewQuestionnaireButton moldOrder={moldOrder} /> : children}
        </div>
      </div>
    );
  };

  const renderCurrentAssigneeInfo = () => {
    const currentAssigneeName = getCurrentAssigneeName();
    const underlineStyle = isCurrentAssignee ? 'border-b border-slate-200 pb-4 mb-4' : '';
    const titleLabel = isCurrentAssignee ? 'ผู้รับผิดชอบ:' : 'ผู้รับผิดชอบขั้นถัดไป:';
    return (
      <div className={`w-full ${underlineStyle}`}>
        {/* Single row layout: Picture | User Info Column | Button */}
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Avatar - fixed size */}
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md">
              <UserProfileImage currentAssignee={null} />
            </div>
          </div>

          {/* User info column - takes available space */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-col justify-center items-start gap-1">
              <div className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                {titleLabel}
              </div>
              <div className="text-sm sm:text-base text-slate-900 truncate font-medium">
                {currentAssigneeName}
              </div>
            </div>
          </div>

          {/* Compact button */}
          {assigneeList.length > 0 && (
            <div className="flex-shrink-0">
              <button
                onClick={() => setIsAssigneeModalOpen(true)}
                className="group flex items-center gap-1 px-1.5 py-1 sm:px-3 sm:py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-semibold rounded-md sm:rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 active:scale-95 border border-blue-400"
              >
                <UserIcon className="h-3 w-3 transition-transform group-hover:scale-110" />
                <span className="hidden sm:inline">เปลี่ยน</span>
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  const MoldOrderStatusInformation = () => {
    return (<div className="flex flex-col gap-1 sm:flex-row sm:gap-0 border-b border-slate-200 pb-4 mb-4 items-center">
      <b className="pr-2">ใบงานอยู่ในขั้นตอน:</b> <MoldOrderStatus moldOrder={moldOrder} />
    </div>)
  }

  // Helper function to render compact next assignee information (for non-approval workflows)
  const CompactNextAssigneeInfo = () => {
    return (
      <div className="p-4 sm:p-5 border-2 border-slate-200 rounded-xl mb-3">
        {isCurrentAssignee && <MoldOrderStatusInformation />}
        {renderCurrentAssigneeInfo()}
        {children}
      </div>
    );
  };

  const showApprovalWorkflow = approvalInfo?.approvalRequired && approvalInfo.approverSlots.length > 0;

  return (
    <div className="w-full">
      {/* Unified Current Assignee and Approval Information */}
      {showApprovalWorkflow ? (
        <CompactApprovalWorkflow />
      ) : (
        <CompactNextAssigneeInfo />
      )}

      {/* Assignee Change Modal */}
      <NextAssigneeModal
        isOpen={isAssigneeModalOpen}
        onClose={() => setIsAssigneeModalOpen(false)}
        onConfirm={handleAssigneeChange}
        assignees={assigneeList || []}
        newAssignee={assignee}
        isCurrentAssignee={true}
      />
    </div>
  )
}

export default NextAssigneeInfo;